'use client';

import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import type { ReactNode} from 'react';
import { createContext, useContext, useCallback, useEffect } from 'react';
import { 
  getUserFriendlyMessage, 
  getErrorLevel, 
  isNetworkError,
  isValidationError,
  assertError
} from 'types/errors';

interface User {
  id: string;
  email?: string;
  name?: string;
}

interface ErrorContextValue {
  // ユーザーコンテキストの設定
  setUserContext: (user: User | null) => void;
  
  // エラータグの設定
  setErrorTag: (key: string, value: string) => void;
  
  // エラーコンテキストのクリア
  clearErrorContext: () => void;
  
  // エラーハンドリング
  handleError: (
    error: unknown,
    options?: {
      component?: string;
      action?: string;
      metadata?: Record<string, unknown>;
      showNotification?: boolean;
      notificationMessage?: string;
    }
  ) => void;
  
  // バリデーションエラーの処理
  handleValidationError: (
    error: unknown,
    options?: {
      component?: string;
      action?: string;
    }
  ) => void;
  
  // ネットワークエラーの処理
  handleNetworkError: (
    error: unknown,
    options?: {
      component?: string;
      action?: string;
      retryable?: boolean;
    }
  ) => void;
}

const ErrorContext = createContext<ErrorContextValue | null>(null);

interface ErrorProviderProps {
  children: ReactNode;
}

export function ErrorProvider({ children }: ErrorProviderProps) {
  const { enqueueSnackbar } = useSnackbar();

  // ユーザーコンテキストの設定
  const setUserContext = useCallback((user: User | null) => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });
    } else {
      Sentry.setUser(null);
    }
  }, []);

  // エラータグの設定
  const setErrorTag = useCallback((key: string, value: string) => {
    Sentry.setTag(key, value);
  }, []);

  // エラーコンテキストのクリア
  const clearErrorContext = useCallback(() => {
    Sentry.configureScope(scope => {
      scope.clear();
    });
  }, []);

  // 汎用エラーハンドリング
  const handleError = useCallback((
    error: unknown,
    options: {
      component?: string;
      action?: string;
      metadata?: Record<string, unknown>;
      showNotification?: boolean;
      notificationMessage?: string;
    } = {}
  ) => {
    try {
      assertError(error);
      
      const {
        component = 'unknown',
        action = 'unknown',
        metadata = {},
        showNotification = true,
        notificationMessage
      } = options;

      // Sentryにエラーを送信
      Sentry.withScope((scope) => {
        scope.setLevel(getErrorLevel(error));
        scope.setContext('frontend', {
          component,
          action,
          metadata,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        });
        scope.setTag('error.frontend', 'true');
        scope.setTag('error.component', component);
        scope.setTag('error.action', action);
        
        Sentry.captureException(error);
      });

      // ユーザー通知
      if (showNotification) {
        const message = notificationMessage || getUserFriendlyMessage(error);
        const severity = getErrorLevel(error) === 'error' ? 'error' : 'warning';
        
        enqueueSnackbar(message, { 
          variant: severity,
          autoHideDuration: isNetworkError(error) ? 5000 : 3000,
        });
      }

      // 開発環境でのログ出力
      if (process.env.NODE_ENV === 'development') {
        console.group(`🚨 Frontend Error (${component})`);
        console.error('Error:', error);
        console.error('Context:', { component, action, metadata });
        console.groupEnd();
      }
    } catch (assertionError) {
      // エラーアサーションが失敗した場合の処理
      console.error('Error assertion failed:', assertionError);
      enqueueSnackbar('予期しないエラーが発生しました', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // バリデーションエラーの専用処理
  const handleValidationError = useCallback((
    error: unknown,
    options: {
      component?: string;
      action?: string;
    } = {}
  ) => {
    if (isValidationError(error)) {
      const fieldMessages = Object.entries(error.fields)
        .map(([field, message]) => `${field}: ${message}`)
        .join(', ');
      
      handleError(error, {
        ...options,
        showNotification: true,
        notificationMessage: `入力エラー: ${fieldMessages}`,
      });
    } else {
      handleError(error, options);
    }
  }, [handleError]);

  // ネットワークエラーの専用処理
  const handleNetworkError = useCallback((
    error: unknown,
    options: {
      component?: string;
      action?: string;
      retryable?: boolean;
    } = {}
  ) => {
    const { retryable = true } = options;
    
    handleError(error, {
      ...options,
      showNotification: true,
      notificationMessage: retryable 
        ? 'ネットワークエラーが発生しました。しばらく待ってから再試行してください。'
        : 'ネットワーク接続を確認してください。',
      metadata: {
        ...options,
        retryable,
      }
    });
  }, [handleError]);

  // ページ離脱時のクリーンアップ
  useEffect(() => {
    const handleBeforeUnload = () => {
      // 必要に応じてコンテキストをクリア
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // 未処理のPromise拒否をキャッチ
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;

      // IndexedDBエラーはユーザーに表示しない（ブラウザ環境の一時的な問題のため）
      // Firebase Authが内部的にIndexedDBを使用しており、このエラーが発生することがある
      const isIndexedDBError =
        error instanceof DOMException &&
        (error.message.includes('IndexedDB') ||
         error.message.includes('Indexed Database') ||
         error.name === 'UnknownError');

      if (isIndexedDBError) {
        // Sentryにはログするがトーストは表示しない
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setTag('error.type', 'indexeddb');
          scope.setTag('error.suppressed', 'true');
          Sentry.captureException(error);
        });

        if (process.env.NODE_ENV === 'development') {
          console.warn('[IndexedDB Error - Suppressed]', error.message);
        }
        return;
      }

      handleError(error, {
        component: 'global',
        action: 'unhandledPromiseRejection',
        metadata: {
          type: 'unhandledPromiseRejection',
        }
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [handleError]);

  const value: ErrorContextValue = {
    setUserContext,
    setErrorTag,
    clearErrorContext,
    handleError,
    handleValidationError,
    handleNetworkError,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
}

// カスタムフック
export function useErrorContext(): ErrorContextValue {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useErrorContext must be used within ErrorProvider');
  }
  return context;
}

// 便利なヘルパーフック
export function useErrorHandler() {
  const { handleError } = useErrorContext();
  return handleError;
}

export function useValidationErrorHandler() {
  const { handleValidationError } = useErrorContext();
  return handleValidationError;
}

export function useNetworkErrorHandler() {
  const { handleNetworkError } = useErrorContext();
  return handleNetworkError;
}